import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, type MachineryInput } from '../api/client'
import { invalidateMachinery } from '../api/invalidate'
import { requireAccessToken } from '../auth/msal'
import './MachineryDetail.css'

/**
 * Adding one machine by hand.
 *
 * A page rather than a modal, like the vehicle add form: entering eleven fields
 * is not a transient gesture, and as a route it survives Back.
 *
 * The category is a free-text field with the existing ones offered as a datalist,
 * not a closed dropdown. The client's list grows — 42 categories came out of one
 * sheet — and refusing a category nobody has used yet would mean refusing a real
 * machine.
 */
export function MachineryNew() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<MachineryInput>({
    categoryName: '',
    assetCode: '',
    serialNumber: '',
    registrationNumberCanonical: '',
    make: '',
    model: '',
    manufactureYear: null,
    siteNameRaw: '',
    altSiteName: '',
    remarks: '',
    remarksAlt: '',
  })

  const facets = useQuery({
    queryKey: ['machinery-facets'],
    queryFn: () => api.getMachineryFacets(),
    staleTime: 5 * 60 * 1000,
  })

  const create = useMutation({
    mutationFn: async () => {
      // At the top of the click, while the gesture's user activation is live.
      await requireAccessToken()

      const cleaned = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v]),
      ) as unknown as MachineryInput

      return api.createMachine({ ...cleaned, categoryName: form.categoryName.trim() })
    },
    onSuccess: async (result) => {
      await invalidateMachinery(queryClient)
      navigate(`/machinery/${result.machineryId}`, { replace: true })
    },
    onError: (e: Error) => setError(e.message),
  })

  function set<K extends keyof MachineryInput>(key: K, value: MachineryInput[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  return (
    <div className="mdet">
      <button type="button" className="mdet__back" onClick={() => navigate('/machinery')}>
        ← Machinery
      </button>

      <header className="mdet__head">
        <div>
          <h1>Add a machine</h1>
          <p className="mdet__sub">
            Only the category is required. Everything else can be filled in later —
            a blank here is a blank on the register, not a guess.
          </p>
        </div>
      </header>

      {error && (
        <p className="mdet__error" role="alert">
          {error}
        </p>
      )}

      <form
        className="mdet__form"
        onSubmit={(e) => {
          e.preventDefault()
          setError(null)
          create.mutate()
        }}
      >
        <label className="mdet__field">
          <span>
            Category<b aria-hidden="true"> *</b>
          </span>
          <input
            list="machinery-categories"
            value={form.categoryName}
            required
            onChange={(e) => set('categoryName', e.target.value)}
          />
          {/* Offered, not enforced. A category that is not on the list yet is a
              machine the client owns and we have not seen. */}
          <datalist id="machinery-categories">
            {(facets.data?.categories ?? []).map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
          <small>Type a new one if it is not in the list.</small>
        </label>

        <Field label="Asset code" value={form.assetCode ?? ''} onChange={(v) => set('assetCode', v)} />
        <Field
          label="Serial number"
          value={form.serialNumber ?? ''}
          onChange={(v) => set('serialNumber', v)}
          hint="The asset code and serial together are how a re-import recognises this machine."
        />
        <Field
          label="Registration"
          value={form.registrationNumberCanonical ?? ''}
          onChange={(v) => set('registrationNumberCanonical', v)}
          hint="Only if it carries an RTO plate. Links it to the vehicle register."
        />
        <Field label="Make" value={form.make ?? ''} onChange={(v) => set('make', v)} />
        <Field label="Model" value={form.model ?? ''} onChange={(v) => set('model', v)} />
        <Field
          label="Year"
          type="number"
          value={form.manufactureYear ? String(form.manufactureYear) : ''}
          onChange={(v) => set('manufactureYear', v ? Number(v) : null)}
        />
        <Field label="Site" value={form.siteNameRaw ?? ''} onChange={(v) => set('siteNameRaw', v)} />
        <Field label="Remark" value={form.remarks ?? ''} onChange={(v) => set('remarks', v)} />

        <div className="mdet__formactions">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={create.isPending || !form.categoryName.trim()}
          >
            {create.isPending ? 'Saving…' : 'Add machine'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  hint?: string
}) {
  return (
    <label className="mdet__field">
      <span>{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
      {hint && <small>{hint}</small>}
    </label>
  )
}
